import React, { useContext, useEffect, useState } from 'react';
import { FormikTouched, FormikErrors } from 'formik';
import {
  Checkbox,
  Dropdown,
  SkeletonText,
  Button,
  TextInput,
  Tag,
  TagSkeleton
} from 'carbon-components-react';
import { useSelector } from 'react-redux';
import { currentUser } from 'redux/session/selectors';
import {
  Add16,
  ArrowRight16,
  Copy16,
  Edit16,
  WarningFilled16
} from '@carbon/icons-react';
import { LocalStoreContext } from '../../../localStore/context';
import {
  LodgeEnqyiryInitialValues,
  EnquiryShape
} from '../../../localStore/types';
import { workPlaceLocItems, stateListItems } from 'app/contants';

const MapLocationForm: React.FC<Props> = ({
  goNext,
  onRedraw,
  onMultipleDraw,
  values,
  setFieldValue,
  setFieldTouched,
  handleChange,
  handleBlur,
  onClickCopyBtn,
  copyBtnDisabled,
  addressLine1InputRef,
  refineAddress
}) => {
  const user = useSelector(currentUser);
  const [addressEmptyAmount, setAddressEmptyAmount] = useState<number>(0);
  const localStore = useContext(LocalStoreContext);
  const {
    shapes,
    shapeArea,
    assetOwnerNumLoading,
    assetOwnerNum,
    geocodeLoading,
    restriction,
    duplicateEnquiry
  } = localStore;

  const onCheckBoxChange = (value: boolean, id: string) => {
    const filteredLocation = values.locations.filter(
      (item: string) => item !== id
    );

    if (value) {
      filteredLocation.push(id);
    }
    setFieldTouched('locations', true);
    setFieldValue('locations', filteredLocation);
  };

  const getSelectedItem = (list: any[], val: string | undefined) => {
    return val ? list.find((item) => item.value === val) : undefined;
  };

  useEffect(() => {
    if (duplicateEnquiry?.location_address) {
      const addressLine1 =
        duplicateEnquiry.location_address?.Street ||
        duplicateEnquiry.location_address?.line1;
      const addressLocality =
        duplicateEnquiry.location_address?.Suburb ||
        duplicateEnquiry.location_address?.locality;
      const addressState =
        duplicateEnquiry.location_address?.State ||
        duplicateEnquiry.location_address?.state;
      const addressPostcode =
        duplicateEnquiry.location_address?.Postcode ||
        duplicateEnquiry.location_address?.postcode;
      setFieldValue('addressLine1', addressLine1);
      setFieldValue('addressLocality', addressLocality);
      setFieldValue('addressState', addressState);
      setFieldValue('addressPostcode', addressPostcode);
    } else {
      if (shapes.length === 1) {
        const address = shapes[0].address;
        setFieldValue('addressLine1', address?.line1);
        setFieldValue('addressLocality', address?.locality);
        setFieldValue('addressState', address?.state);
        setFieldValue('addressPostcode', address?.postcode);
      }
    }
  }, [shapes, setFieldValue, duplicateEnquiry]);

  useEffect(() => {
    if (shapes.length === 1) {
      setAddressEmptyAmount(
        !values.addressLine1 ||
          !values.addressLocality ||
          !values.addressState ||
          !values.addressPostcode
          ? 1
          : 0
      );
    } else {
      const emptyAmt = shapes.reduce((amt: number, shape: EnquiryShape) => {
        if (
          !shape.address?.line1 ||
          !shape.address?.locality ||
          !shape.address?.state ||
          !shape.address?.postcode
        ) {
          amt += 1;
        }
        return amt;
      }, 0);
      setAddressEmptyAmount(emptyAmt);
    }
  }, [shapes, values]);

  return (
    <div className="enquiry-form--location">
      <div className="form-header">
        <div className="location-legends">
          {shapeArea ? (
            <Tag type="blue" title="Area cover">
              {shapeArea > 0.01
                ? `${shapeArea.toFixed(2)} km²`
                : `${Number(shapeArea * 1e6).toFixed(2)} m²`}
            </Tag>
          ) : (
            <TagSkeleton />
          )}
          {shapes.length === 1 &&
            !assetOwnerNumLoading &&
            typeof assetOwnerNum === 'number' && (
              <Tag type="green" title="Asset owner">
                {assetOwnerNum > 0
                  ? `${assetOwnerNum} Asset owner${
                      assetOwnerNum > 1 ? 's' : ''
                    }`
                  : 'No asset owner'}
              </Tag>
            )}
          {shapes.length === 1 && assetOwnerNumLoading && <TagSkeleton />}
        </div>
        <div className="location-buttons">
          {!user?.appFeatures?.includes('DISABLED_LARGE_SCALE_ENQUIRIES') && (
            <Button
              kind="ghost"
              renderIcon={Add16}
              onClick={() => onMultipleDraw()}
            >
              Add areas
            </Button>
          )}
          <Button kind="ghost" onClick={() => onRedraw()}>
            Redraw
          </Button>
        </div>
      </div>
      <div className="form-body">
        <div className="form-input-section">
          <div className="address-header">
            <div className="bx--label">Approximate address</div>
          </div>
          <div
            className={`address-body${shapes.length > 1 ? ' multiple' : ''}`}
          >
            {shapes.length > 0 && shapes[0].address ? (
              <>
                {shapes.length === 1 ? (
                  <>
                    <div className="bx--form-item bx--text-input-wrapper">
                      <div className="bx--text-input__field-outer-wrapper">
                        <div className="bx--text-input__field-wrapper">
                          <input
                            ref={addressLine1InputRef}
                            id="addressLine1"
                            placeholder="Street"
                            type="text"
                            className="bx--text-input bx--text__input address-line-1"
                            title="Street"
                            name="addressLine1"
                            data-testid="addressLine1"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            onInput={() =>
                              setFieldTouched('addressLine1', true)
                            }
                            value={values.addressLine1 || ''}
                          />
                          <Edit16
                            className="bx--date-picker__icon"
                            onClick={() => refineAddress()}
                          />
                        </div>
                      </div>
                    </div>
                    {shapes[0].address?.locality ? (
                      <div className="address-body-content static-value">
                        {values.addressLocality}
                      </div>
                    ) : (
                      <TextInput
                        id="addressLocality"
                        name="addressLocality"
                        placeholder="Suburb/town"
                        hideLabel={true}
                        labelText=""
                        data-testid="addressLocality"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onInput={() => setFieldTouched('addressLocality', true)}
                        value={values.addressLocality || ''}
                      />
                    )}
                    <div className="address-body-content state-and-postcode-input">
                      {shapes[0].address?.state ? (
                        <div className="state sub-content static-value">
                          {values.addressState}
                        </div>
                      ) : (
                        <Dropdown
                          id="addressState"
                          className="state"
                          items={stateListItems}
                          itemToString={(item: any) => (item ? item.label : '')}
                          label="Choose an option"
                          titleText=""
                          selectedItem={getSelectedItem(
                            stateListItems,
                            values.addressState
                          )}
                          onChange={({ selectedItem }) => {
                            setFieldValue('addressState', selectedItem.value);
                            setFieldTouched('addressState', true);
                          }}
                        />
                      )}
                      {shapes[0].address?.postcode ? (
                        <div className="postcode sub-content static-value">
                          {values.addressPostcode}
                        </div>
                      ) : (
                        <TextInput
                          id="addressPostcode"
                          name="addressPostcode"
                          placeholder="Postcode"
                          className="postcode"
                          hideLabel={true}
                          labelText=""
                          data-testid="addressPostcode"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          onInput={() =>
                            setFieldTouched('addressPostcode', true)
                          }
                          value={values.addressPostcode || ''}
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`address-body-content static-value multiple${
                        addressEmptyAmount > 0 ? ' invalid' : ''
                      }`}
                    >
                      <span>Multiple addresses (Select shape to update)</span>
                      {addressEmptyAmount > 0 && <WarningFilled16 />}
                    </div>
                    {addressEmptyAmount > 0 && !geocodeLoading && (
                      <span className="text-error">
                        {' '}
                        {addressEmptyAmount} invalid address. Select shape to
                        override.
                      </span>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <SkeletonText />
                <SkeletonText />
                <SkeletonText />
              </>
            )}
          </div>
        </div>
        <div className="form-input-section">
          <div className="bx--label">Workplace location(s)</div>
          <div className="work-place-locations">
            {workPlaceLocItems.map((item: any) => {
              return (
                <div className="work-place-location-item" key={item.id}>
                  <Checkbox
                    checked={values.locations.includes(item.id)}
                    labelText={item.label}
                    id={item.id}
                    onChange={onCheckBoxChange}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="form-footer">
        <div className="form-progress--wrapper">
          <div className="form-progress completed"></div>
          <div className="form-progress"></div>
          <div className="form-progress"></div>
        </div>
        <div className="form-footer-buttons">
          <Button
            className="copy-btn"
            kind="tertiary"
            renderIcon={Copy16}
            onClick={onClickCopyBtn}
            disabled={copyBtnDisabled}
          >
            Copy from last
          </Button>
          <Button
            className="continue-btn"
            kind="secondary"
            renderIcon={ArrowRight16}
            onClick={() => goNext()}
            disabled={
              addressEmptyAmount > 0 ||
              values.locations.length <= 0 ||
              (restriction &&
                (!restriction.allow ||
                  (restriction.total > 0 &&
                    shapes.length + restriction.used > restriction.total)))
            }
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

type Props = {
  touched: FormikTouched<LodgeEnqyiryInitialValues>;
  errors: FormikErrors<LodgeEnqyiryInitialValues>;
  values: LodgeEnqyiryInitialValues;
  handleChange: (e: React.ChangeEvent) => void;
  handleBlur: (e: React.FocusEvent) => void;
  onClickCopyBtn: (e: React.MouseEvent) => void;
  copyBtnDisabled: boolean;
  addressLine1InputRef: React.RefObject<HTMLInputElement>;
  setFieldValue: Function;
  setFieldTouched: Function;
  onRedraw: Function;
  onMultipleDraw: Function;
  goNext: Function;
  refineAddress: Function;
};

export default React.memo(MapLocationForm);
